import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Settings,
  ChevronRight,
  ChevronDown,
  DollarSign,
  Smartphone,
  Users,
  Wallet,
  Building,
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Phone,
  Globe,
  RefreshCw,
  Bell,
  Lock,
  Unlock,
  Info,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_layout/settings/operations')({
  component: OperationsPlaybookPage,
});

/* ── Data ─────────────────────────────────────────────── */

const PAYMENT_SCENARIOS = [
  { id: 1, scenario: 'Automatic ZAAD deduction success — full installment', channel: 'ZAAD Automatic Deduction', status: 'PAID', action: 'Update balance and contract; keep device active; send receipt/SMS.', message: 'Your installment of $X has been received successfully. Remaining balance: $Y.', owner: 'System', color: 'bg-emerald-500/10' },
  { id: 2, scenario: 'Partial ZAAD deduction — partial amount collected', channel: 'ZAAD Automatic Deduction', status: 'PARTIALLY PAID', action: 'Update balance; show remaining amount due; keep device active if minimum threshold met.', message: 'Partial payment received. Remaining balance is $X due by [date].', owner: 'System', color: 'bg-amber-500/10' },
  { id: 3, scenario: 'Automatic deduction failed — insufficient balance', channel: 'ZAAD Automatic Deduction', status: 'DUE / GRACE', action: 'Retry as per policy; send reminder; start grace period countdown.', message: 'We could not process your installment due to insufficient ZAAD balance.', owner: 'System', color: 'bg-orange-500/10' },
  { id: 4, scenario: 'Multiple deduction failures after grace period', channel: 'ZAAD Automatic Deduction', status: 'OVERDUE', action: 'Mark overdue; trigger collections workflow; send warning.', message: 'Your payment is overdue. Please pay to avoid device restriction.', owner: 'System / Branch Mgr', color: 'bg-red-500/10' },
  { id: 5, scenario: 'Device restricted after policy threshold', channel: 'System Enforcement', status: 'RESTRICTED', action: 'Apply soft restriction or lock based on policy and overdue amount.', message: 'Your payment is overdue. Device access has been restricted.', owner: 'System', color: 'bg-red-500/10' },
  { id: 6, scenario: 'Payment received after restriction', channel: 'ZAAD / Cash / Branch', status: 'RESTORED', action: 'Unlock device automatically after confirmation; update balance.', message: 'Payment received. Your device has been restored.', owner: 'System', color: 'bg-emerald-500/10' },
  { id: 7, scenario: 'Offline cash payment via agent', channel: 'Agent Cash', status: 'PAID', action: 'Record payment offline; generate receipt; sync when online; update contract.', message: 'Cash payment of $X received. Your balance is now $Y.', owner: 'Agent / System', color: 'bg-blue-500/10' },
  { id: 8, scenario: 'Cash payment at branch/office', channel: 'Branch Cash', status: 'PAID', action: 'Record and reconcile payment; update balance; issue official receipt.', message: 'Payment received. Thank you.', owner: 'Branch Mgr / Cashier', color: 'bg-blue-500/10' },
  { id: 9, scenario: 'Third-party payment on behalf of customer', channel: 'ZAAD / Cash / Branch', status: 'PAID', action: 'Credit customer contract; record payer details; update status.', message: 'Payment received from [payer]. Your balance is now $X.', owner: 'System / Agent', color: 'bg-violet-500/10' },
  { id: 10, scenario: 'Overpayment received', channel: 'ZAAD / Cash', status: 'CREDIT', action: 'Apply excess to future installments or customer wallet based on policy.', message: 'You have overpaid by $X. This has been applied to your next payment.', owner: 'System / Finance', color: 'bg-violet-500/10' },
  { id: 11, scenario: 'Failed payment attempt', channel: 'ZAAD / Self-Payment', status: 'FAILED', action: 'Do not update balance; notify customer to retry; log failed attempt.', message: 'Payment failed. Please retry or contact support.', owner: 'System', color: 'bg-red-500/10' },
  { id: 12, scenario: 'Duplicate payment detected', channel: 'ZAAD / Cash', status: 'REVIEW', action: 'Flag for reconciliation; apply as credit or refund after approval.', message: 'Duplicate payment detected. We are reviewing your account.', owner: 'Finance / Branch Mgr', color: 'bg-amber-500/10' },
  { id: 13, scenario: 'Payment not matched to contract', channel: 'ZAAD / Cash', status: 'SUSPENSE', action: 'Hold payment in suspense; operations to match to customer/contract.', message: 'Payment received but requires verification before posting.', owner: 'Finance / Ops', color: 'bg-amber-500/10' },
  { id: 14, scenario: 'Final installment paid', channel: 'ZAAD / Cash', status: 'COMPLETE', action: 'Close contract; disengage financing lock; transfer ownership; send completion SMS.', message: 'Congratulations. Your contract is complete and the device is fully yours.', owner: 'System / Tenant Admin', color: 'bg-emerald-500/10' },
];

const PAYMENT_CHANNELS = [
  { name: 'Automatic ZAAD Deduction', how: 'Scheduled installment is deducted from customer ZAAD wallet automatically.', controls: 'API status monitoring, retry queue, payment reference, SMS confirmation.', risk: 'No manual handling, real-time posting, duplicate detection.', owner: 'System', icon: <Wallet className="w-5 h-5" />, color: 'from-teal-500 to-teal-600' },
  { name: 'Agent Cash Collection', how: 'Customer pays cash to field/branch agent; agent records payment in app.', controls: 'Offline mode, GPS capture, receipt number, sync queue, agent audit trail.', risk: 'Supervisor review for edits, daily reconciliation, fraud alerts.', owner: 'Agent / Branch Mgr', icon: <Users className="w-5 h-5" />, color: 'from-blue-500 to-blue-600' },
  { name: 'Branch Cash Payment', how: 'Customer pays at branch/office counter.', controls: 'Official receipt, cashier reconciliation, branch cash register, same-day posting.', risk: 'Dual control, manager review, end-of-day cash report.', owner: 'Branch Mgr', icon: <Building className="w-5 h-5" />, color: 'from-amber-500 to-amber-600' },
  { name: 'Customer Self-Payment', how: 'Customer initiates payment manually through USSD and App.', controls: 'Payment reference, callback confirmation, real-time balance update.', risk: 'API validation, duplicate protection, suspense handling.', owner: 'System', icon: <Phone className="w-5 h-5" />, color: 'from-violet-500 to-violet-600' },
  { name: 'Third-Party Payment', how: 'Family/friend pays on behalf of customer using ZAAD or cash.', controls: 'Record payer details, payer MSISDN, customer contract mapping.', risk: 'Confirm customer contract, payer audit trail, SMS to customer.', owner: 'System / Agent', icon: <Globe className="w-5 h-5" />, color: 'from-red-500 to-red-600' },
];

const ZAAD_FLOW = [
  { step: 1, action: 'Schedule trigger runs', input: 'Repayment schedule, due date/time, customer MSISDN', output: 'Deduction job created', failure: 'If scheduler fails, alert operations and retry job.' },
  { step: 2, action: 'Call ZAAD deduction API', input: 'Customer wallet MSISDN, amount due, contract reference', output: 'API request submitted', failure: 'If timeout/API error, queue retry and keep account in grace state.' },
  { step: 3, action: 'Receive ZAAD response', input: 'Success, insufficient funds, wallet blocked, timeout, partial deduction', output: 'Payment outcome classified', failure: 'Map response to scenario and log raw response.' },
  { step: 4, action: 'Post payment to contract', input: 'Confirmed payment reference and amount', output: 'Balance updated; installment status updated', failure: 'If posting fails, put payment in reconciliation/suspense.' },
  { step: 5, action: 'Execute device action', input: 'Current balance, overdue amount, lock policy', output: 'Keep active, warn, lock, unlock or disengage', failure: 'If device command fails, queue command and alert device operations.' },
  { step: 6, action: 'Notify customer', input: 'Event type and updated balance', output: 'SMS/WhatsApp sent', failure: 'If notification fails, retry and log delivery status.' },
];

const CASH_FLOW = [
  { step: 1, stage: 'Customer pays cash', desc: 'Customer pays installment to agent or branch cashier.', requirement: 'Capture customer/contract ID, amount, payment channel.', control: 'Receipt must be generated or queued.' },
  { step: 2, stage: 'Agent records payment', desc: 'Agent enters payment in Mazeed Pay agent app.', requirement: 'Offline mode enabled with local encrypted storage.', control: 'Capture agent ID, GPS, timestamp and device ID.' },
  { step: 3, stage: 'Payment saved locally', desc: 'If offline, payment is stored securely until connectivity returns.', requirement: 'Sync queue with unique transaction reference.', control: 'Prevent duplicate entries and tampering.' },
  { step: 4, stage: 'Sync to server', desc: 'When online, app syncs queued payment to server.', requirement: 'Server validates receipt, amount, contract and sequence.', control: 'Sync success/failure logged.' },
  { step: 5, stage: 'Contract updated', desc: 'Payment updates balance and installment status.', requirement: 'Recalculate overdue amount and device action.', control: 'All changes audited.' },
  { step: 6, stage: 'Customer notified', desc: 'Customer receives SMS confirmation after payment posts.', requirement: 'SMS template triggered by payment confirmation.', control: 'SMS delivery status logged.' },
  { step: 7, stage: 'Daily reconciliation', desc: 'Branch/finance compares cash collected vs synced payments.', requirement: 'Reconciliation dashboard and cash variance report.', control: 'Supervisor approval for corrections.' },
];

/* ── Status Badge ─────────────────────────────────────── */

const statusColor: Record<string, string> = {
  PAID: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  'PARTIALLY PAID': 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  'DUE / GRACE': 'bg-orange-500/15 text-orange-700 border-orange-500/30',
  OVERDUE: 'bg-red-500/15 text-red-700 border-red-500/30',
  'RESTRICTED': 'bg-red-500/15 text-red-700 border-red-500/30',
  RESTORED: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  CREDIT: 'bg-violet-500/15 text-violet-700 border-violet-500/30',
  FAILED: 'bg-red-500/15 text-red-700 border-red-500/30',
  REVIEW: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  SUSPENSE: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  COMPLETE: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
};

/* ── Collapsible Section ──────────────────────────────── */

function Section({
  title,
  subtitle,
  icon,
  gradient,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  gradient: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="shadow-md border-0">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
            >
              {icon}
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {title}
                {count != null && (
                  <Badge variant="secondary" className="text-xs">
                    {count}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {subtitle}
              </CardDescription>
            </div>
          </div>
          {open ? (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

/* ── Flow Pipeline ────────────────────────────────────── */

function FlowPipeline({
  steps,
}: {
  steps: { step: number; label: string; detail: string; sub: string }[];
}) {
  return (
    <div className="space-y-3">
      {steps.map((s, i) => (
        <div key={s.step} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 text-white text-xs font-bold flex items-center justify-center shadow">
              {s.step}
            </div>
            {i < steps.length - 1 && (
              <div className="w-0.5 flex-1 bg-primary/20 mt-1" />
            )}
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm font-semibold">{s.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.detail}</p>
            <p className="text-[10px] text-red-500/80 mt-1 italic">
              ⚠ {s.sub}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────── */

function OperationsPlaybookPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Operations Playbook
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          End-to-end payment scenarios, collection channels, and operational
          flows from the BNPL operating model
        </p>
      </div>

      {/* Payment Scenarios */}
      <Section
        title="Payment Scenarios"
        subtitle="14 end-to-end payment use cases covering ZAAD, cash, partial, failure, restoration and completion"
        icon={<DollarSign className="w-5 h-5" />}
        gradient="from-emerald-500 to-emerald-600"
        count={14}
        defaultOpen
      >
        <div className="overflow-x-auto -mx-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Scenario</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="min-w-[200px]">System Action</TableHead>
                <TableHead>Owner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PAYMENT_SCENARIOS.map((s) => (
                <TableRow key={s.id} className={s.color}>
                  <TableCell className="font-mono text-xs font-bold">
                    {s.id}
                  </TableCell>
                  <TableCell className="text-xs font-medium max-w-[220px]">
                    {s.scenario}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="text-[10px] whitespace-nowrap"
                    >
                      {s.channel}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={cn(
                        'text-[10px]',
                        statusColor[s.status] || 'bg-muted'
                      )}
                    >
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[250px]">
                    {s.action}
                  </TableCell>
                  <TableCell className="text-xs font-medium whitespace-nowrap">
                    {s.owner}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      {/* Payment Channels */}
      <Section
        title="Payment Channels"
        subtitle="Hybrid collection model: digital automatic deductions plus assisted offline cash collections"
        icon={<Wallet className="w-5 h-5" />}
        gradient="from-blue-500 to-blue-600"
        count={5}
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {PAYMENT_CHANNELS.map((ch) => (
            <Card
              key={ch.name}
              className="border shadow-sm hover:shadow-md transition-all duration-200"
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-xl bg-gradient-to-br ${ch.color} text-white shadow`}
                  >
                    {ch.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{ch.name}</p>
                    <Badge variant="outline" className="text-[10px] mt-0.5">
                      {ch.owner}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{ch.how}</p>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-1.5">
                    <Shield className="w-3 h-3 text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-muted-foreground">
                      <span className="font-medium text-foreground">
                        Controls:
                      </span>{' '}
                      {ch.controls}
                    </p>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-muted-foreground">
                      <span className="font-medium text-foreground">
                        Risk:
                      </span>{' '}
                      {ch.risk}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* ZAAD Auto-Deduction Flow */}
      <Section
        title="Automatic ZAAD Deduction Flow"
        subtitle="6-step flow for scheduled wallet deductions and API outcomes"
        icon={<RefreshCw className="w-5 h-5" />}
        gradient="from-teal-500 to-teal-600"
        count={6}
      >
        <FlowPipeline
          steps={ZAAD_FLOW.map((f) => ({
            step: f.step,
            label: f.action,
            detail: `${f.input} → ${f.output}`,
            sub: f.failure,
          }))}
        />
      </Section>

      {/* Offline Cash Flow */}
      <Section
        title="Offline Cash Payment Flow"
        subtitle="Agent and branch cash collection workflow including offline sync, receipts, controls and fraud prevention"
        icon={<DollarSign className="w-5 h-5" />}
        gradient="from-amber-500 to-amber-600"
        count={7}
      >
        <FlowPipeline
          steps={CASH_FLOW.map((f) => ({
            step: f.step,
            label: f.stage,
            detail: `${f.desc} — ${f.requirement}`,
            sub: f.control,
          }))}
        />
      </Section>
    </div>
  );
}
